import React, { createContext, useContext, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { noTransition, transitionIn, transitionOut } from './transitions';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import customEventBus from '@common/scripts/libs/eventBus';
import gsap from 'gsap';
gsap.registerPlugin(ScrollTrigger);
export const AnimatedContext = createContext({});
const AnimatedProvider = (props) => {
    // State
    const [transitionState, setTransitionState] = useState('INITIAL');
    const [fromPLP, setFromPLP] = useState(false);
    const location = useLocation();
    const firstRender = useRef(true);
    const bodyHeight = useRef(null);
    const plpScrollY = useRef(0);
    const headerState = useRef(null);
    const store = {
        enter: null,
        exit: null,
    };
    const playbackHooks = useRef({
        done: [],
        setup: [],
        animate: [],
        appMounted: [],
    });
    // Methods
    const subscribe = (key, cb) => {
        playbackHooks.current[key].push(cb);
    };
    const unsubscribe = (key, cb) => {
        playbackHooks.current[key] = [...playbackHooks.current[key].filter((entry) => entry !== cb)];
    };
    const resetStore = () => {
        store.exit = null;
        store.enter = null;
    };
    const skipAnimation = async (params, TO_PDP, IS_MOBILE) => {
        var _a;
        const { animate, done } = playbackHooks.current;
        const { enter, exit, transition } = params;
        const header = document.getElementsByTagName('header')[0];
        const catalogContainer = document.getElementById('catalog');
        if (TO_PDP) {
            bodyHeight.current = `${catalogContainer.offsetHeight}`;
            plpScrollY.current = window.scrollY;
        }
        else {
            catalogContainer.style.height = `${bodyHeight.current}px`;
        }
        await Promise.all(animate.map(async (cb) => await cb(params)));
        enter.animation.set({
            opacity: 1,
            pointerEvents: 'all',
            y: 0,
            x: 0,
        });
        await Promise.all(done.map(async (cb) => await cb(params)));
        if ((_a = enter === null || enter === void 0 ? void 0 : enter.el) === null || _a === void 0 ? void 0 : _a.current) {
            enter.el.current.style.position = 'static';
        }
        if (TO_PDP) {
            document.documentElement.scrollTop = 0;
            header.classList.add('header--pdp');
        }
        else {
            document.documentElement.scrollTop = plpScrollY.current;
        }
        catalogContainer.style.removeProperty('overflow');
        catalogContainer.style.removeProperty('height');
        exit.done();
        enter.done();
        resetStore();
        if (transition.direction === 'out') {
            header === null || header === void 0 ? void 0 : header.classList.remove('header--pdp');
        }
    };
    //SECTION - TRANSITION ----------------------------------------------------------------------------------------------------------------------------------------
    const transition = async (params) => {
        var _a, _b, _c;
        const { setup, animate, done } = playbackHooks.current;
        const { enter, exit, transition } = params;
        const TO_PDP = transition.direction === 'in';
        //@ts-ignore disable-next-line
        const IS_MOBILE = window.innerWidth < window.mqObj['lg'];
        const header = document.getElementsByTagName('header')[0];
        header.classList.remove('hidden');
        const catalogContainer = document.getElementById('catalog');
        if (TO_PDP) {
            gsap.to(header, {
                y: 0,
            });
            (_a = ScrollTrigger.getById('header-transparency')) === null || _a === void 0 ? void 0 : _a.kill();
            customEventBus.emit('header:setpdp', { isPdp: true });
            bodyHeight.current = `${catalogContainer.offsetHeight}`;
            plpScrollY.current = window.scrollY;
            headerState.current = {
                color: header.dataset.color,
                transparent: header.dataset.transparent,
            };
            //OVERRIDE HEADER COLORS FROM PLP
            customEventBus.emit('header:settrasparency', {
                transparent: false,
                color: 'black',
            });
        }
        else {
            customEventBus.emit('header:setpdp', { isPdp: false });
            catalogContainer.style.height = `${bodyHeight.current}px`;
        }
        const exitScrollTop = window.scrollY;
        //ANCHOR - SETUP ----------------------------------------------------------------------------------------------------------------------------------------
        await Promise.all(setup.map(async (cb) => await cb(params)));
        //check if page is not ready
        if (!((_b = exit === null || exit === void 0 ? void 0 : exit.el) === null || _b === void 0 ? void 0 : _b.current) || !((_c = enter === null || enter === void 0 ? void 0 : enter.el) === null || _c === void 0 ? void 0 : _c.current)) {
            console.warn('ANIMATION SKIPPED FOR AJAX PROBLEM');
            skipAnimation(params, TO_PDP, IS_MOBILE);
            return;
        }
        await Promise.all(animate.map(async (cb) => await cb(params)));
        exit.animation.set({
            top: TO_PDP ? exitScrollTop * -1 + 'px' : 'unset',
            opacity: 1,
            y: 0,
            position: TO_PDP ? 'fixed' : 'relative',
            zIndex: TO_PDP ? 9 : 0,
            backgroundColor: 'white',
            pointerEvents: 'none',
        });
        enter.animation.set({
            opacity: 0,
            y: TO_PDP && !IS_MOBILE ? 0 : 30,
            position: 'relative',
            zIndex: 8,
            pointerEvents: 'none',
        });
        //ANCHOR - ANIMATION ----------------------------------------------------------------------------------------------------------------------------------------
        if (TO_PDP) {
            document.documentElement.scrollTop = 0;
            header.classList.add('header--pdp');
        }
        else {
            document.documentElement.scrollTop = plpScrollY.current;
        }
        if (TO_PDP) {
            exit.animation.start(Object.assign({ opacity: 0 }, transitionOut));
        }
        else {
            exit.animation.start(Object.assign({ opacity: 0, display: 'none' }, noTransition));
        }
        await enter.animation.start(Object.assign({ opacity: 1, y: 0 }, transitionIn));
        // ANCHOR - CLEAN UP ----------------------------------------------------------------------------------------------------------------------------------------
        await Promise.all(done.map(async (cb) => await cb(params)));
        if (!TO_PDP) {
            header.classList.remove('header--pdp');
        }
        catalogContainer.style.height = `auto`;
        // setTransitionState(TO_PDP ? 'END' : 'INITIAL');
        exit.animation.set({
            opacity: 0,
            y: 0,
            position: 'static',
            zIndex: 'unset',
            pointerEvents: 'all',
        });
        enter.animation.set({
            opacity: 1,
            y: 0,
            pointerEvents: 'all',
            position: 'static',
            zIndex: 'unset',
        });
        exit.done();
        enter.done();
        resetStore();
    };
    // !SECTION - END TRANSITION
    const submitEnter = async (enter) => {
        if (firstRender.current) {
            const { appMounted } = playbackHooks.current;
            await Promise.all(appMounted.map(async (cb) => {
                await cb();
            }));
            firstRender.current = false;
        }
        else {
            store.enter = enter;
            ready();
        }
    };
    const submitExit = (exit) => {
        store.exit = exit;
        ready();
    };
    // ANCHOR - Ready --------------------------------------------------------------------
    const ready = async () => {
        const { state } = location;
        const { exit, enter } = store;
        if (!exit || !enter)
            return;
        const animationObject = {
            exit,
            enter,
            viewport: state === null || state === void 0 ? void 0 : state.viewport,
            transition: state === null || state === void 0 ? void 0 : state.transition,
        };
        setTransitionState('TRANSITIONING');
        await transition(animationObject);
        ScrollTrigger.refresh(true);
        setTransitionState('END');
        //@ts-ignore
        if (window.chatObject)
            window.chatObject.value = new Date().getTime();
    };
    const context = {
        submitExit,
        submitEnter,
        subscribe,
        unsubscribe,
        transitionState,
        fromPLP,
        setFromPLP,
    };
    // Render
    return React.createElement(AnimatedContext.Provider, { value: context }, props.children);
};
export const useAnimatedContext = () => {
    return useContext(AnimatedContext);
};
export default AnimatedProvider;
